import { PlusIcon, TrashIcon, CpuChipIcon } from "@heroicons/react/24/outline";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import {
  v2PortfolioSummary,
  v2PortfolioAdd,
  v2PortfolioRemove,
  v2StartAnalyze,
} from "../services/tradingAgentsV2";

export default function Portfolio() {
  const queryClient = useQueryClient();
  const [ticker, setTicker] = useState("");
  const [shares, setShares] = useState("");
  const [avgCost, setAvgCost] = useState("");

  const summaryQuery = useQuery({
    queryKey: ["portfolio-summary"],
    queryFn: v2PortfolioSummary,
    refetchInterval: 30000,
  });

  const addMutation = useMutation({
    mutationFn: v2PortfolioAdd,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["portfolio-summary"] });
      setTicker("");
      setShares("");
      setAvgCost("");
    },
  });

  const removeMutation = useMutation({
    mutationFn: v2PortfolioRemove,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["portfolio-summary"] }),
  });

  const analyzeMutation = useMutation({ mutationFn: v2StartAnalyze });

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    const t = ticker.trim().toUpperCase();
    if (!t || !shares || !avgCost) return;
    addMutation.mutate({ ticker: t, shares: Number(shares), avg_cost: Number(avgCost) });
  };

  const summary = summaryQuery.data;
  const pnlColor = (v: number) => (v >= 0 ? "text-green-600" : "text-red-600");

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { label: "Total Value", value: summary?.total_value, fmt: "$" },
          { label: "Total Cost", value: summary?.total_cost, fmt: "$" },
          { label: "Total P&L", value: summary?.total_pnl, fmt: "$", colored: true },
          { label: "Return", value: summary?.total_pnl_pct, fmt: "%", colored: true },
        ].map((c) => (
          <div key={c.label} className="card">
            <p className="text-sm text-gray-500">{c.label}</p>
            <p
              className={`mt-1 text-2xl font-bold ${
                c.colored && c.value != null ? pnlColor(c.value) : "text-gray-900"
              }`}
            >
              {c.value != null
                ? c.fmt === "$"
                  ? `$${c.value.toLocaleString(undefined, { minimumFractionDigits: 2 })}`
                  : `${c.value.toFixed(2)}%`
                : "—"}
            </p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Add holding form */}
        <div className="card lg:col-span-1">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">Add Holding</h2>
          <form onSubmit={handleAdd} className="space-y-3">
            <Input
              label="Ticker"
              value={ticker}
              onChange={(e) => setTicker(e.target.value)}
              placeholder="AAPL"
              required
            />
            <Input
              label="Shares"
              type="number"
              step="any"
              min="0.01"
              value={shares}
              onChange={(e) => setShares(e.target.value)}
              required
            />
            <Input
              label="Avg Cost ($)"
              type="number"
              step="any"
              min="0"
              value={avgCost}
              onChange={(e) => setAvgCost(e.target.value)}
              required
            />
            {addMutation.isError && (
              <p className="text-sm text-red-600">{(addMutation.error as Error).message}</p>
            )}
            <Button
              type="submit"
              className="w-full"
              isLoading={addMutation.isPending}
              leftIcon={<PlusIcon className="h-5 w-5" />}
            >
              Add Holding
            </Button>
          </form>
        </div>

        {/* Holdings table */}
        <div className="card lg:col-span-2">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">Holdings</h2>
          {!summary || summary.holdings.length === 0 ? (
            <p className="text-sm text-gray-500">No holdings yet. Add a position to get started.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {["Ticker", "Shares", "Avg Cost", "Price", "Value", "P&L", "%", "Agent", ""].map(
                      (h) => (
                        <th key={h} className="px-3 py-2 text-left font-medium text-gray-700">
                          {h}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {summary.holdings.map((h) => (
                    <tr key={h.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-semibold text-gray-900">{h.ticker}</td>
                      <td className="px-3 py-2 text-gray-700">{h.shares}</td>
                      <td className="px-3 py-2 text-gray-700">${h.avg_cost.toFixed(2)}</td>
                      <td className="px-3 py-2 text-gray-700">${h.current_price.toFixed(2)}</td>
                      <td className="px-3 py-2 text-gray-700">
                        ${h.value.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                      <td className={`px-3 py-2 font-medium ${pnlColor(h.pnl)}`}>
                        {h.pnl >= 0 ? "+" : ""}${h.pnl.toFixed(2)}
                      </td>
                      <td className={`px-3 py-2 font-medium ${pnlColor(h.pnl_pct)}`}>
                        {h.pnl_pct >= 0 ? "+" : ""}
                        {h.pnl_pct.toFixed(2)}%
                      </td>
                      <td className="px-3 py-2">
                        {h.last_agent_action ? (
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                              h.last_agent_action.toUpperCase() === "BUY"
                                ? "bg-green-100 text-green-700"
                                : h.last_agent_action.toUpperCase() === "SELL"
                                  ? "bg-red-100 text-red-700"
                                  : "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {h.last_agent_action}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1">
                          <button
                            type="button"
                            onClick={() =>
                              analyzeMutation.mutate({
                                ticker: h.ticker,
                                trade_date: new Date().toISOString().slice(0, 10),
                              })
                            }
                            className="rounded p-1 text-gray-400 hover:bg-primary-50 hover:text-primary-600"
                            title="Get Agent Opinion"
                            disabled={analyzeMutation.isPending}
                          >
                            <CpuChipIcon className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => removeMutation.mutate(h.id)}
                            className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                            title="Remove"
                            disabled={removeMutation.isPending}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
